import type { EnhancedTableRowType, TableColumnType, TableRowType } from '../../../../grid/reduxStore/table.types';
import { ColumnTypeIdentifier, RowTypesIdentifier } from '../../../../grid/reduxStore/table.types';
import { isGeneratedRow } from '../../../../grid/reduxStore/crmRowMarkers';
import { getPriceFromRow } from '@proposify/pricing-engine';
import type { TableRowInput, TableColumnInput, BillingFrequency } from '@proposify/pricing-engine';

function extractNumericValue(text: string): number | null {
  if (!text || text.trim() === '') return null;

  const cleaned = text
    .replace(/[$€£¥₹]/g, '')
    .replace(/,/g, '')
    .replace(/\s+/g, '')
    .trim();

  if (cleaned === '') return null;

  const number = parseFloat(cleaned);
  return isNaN(number) ? null : number;
}

function extractRawValueByColumnType(value: string, columnType: ColumnTypeIdentifier): string | null {
  const withoutHtml = value.replace(/<[^>]*>/g, '').trim();

  switch (columnType) {
    case ColumnTypeIdentifier.PRICE:
    case ColumnTypeIdentifier.MULTIPLIER: {
      const numericValue = extractNumericValue(withoutHtml);
      return numericValue !== null ? String(numericValue) : null;
    }

    case ColumnTypeIdentifier.TEXT:
      return withoutHtml || null;

    // Subtotals are engine output, not input; handler/optional carry no values.
    case ColumnTypeIdentifier.SUBTOTAL:
    case ColumnTypeIdentifier.HANDLER:
    case ColumnTypeIdentifier.OPTIONAL:
      return null;

    default:
      return withoutHtml || null;
  }
}

function isEngineDiscount(discount: TableRowType['discount']): discount is NonNullable<TableRowInput['discount']> {
  return (
    typeof discount === 'object' &&
    discount !== null &&
    (discount.type === 'percentage' || discount.type === 'fixed') &&
    discount.value != null
  );
}

function toEngineOverride(value: TableRowType['manualSubtotalOverride']): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (trimmed === '' || trimmed.startsWith('<')) return null;
  return trimmed;
}

function toEngineRowType(rowType: TableRowType['rowType']): RowTypesIdentifier {
  switch (rowType) {
    case RowTypesIdentifier.HEADER:
      return RowTypesIdentifier.HEADER;
    case RowTypesIdentifier.FOOTER:
    case 'Subtotal':
    case 'Discount':
    case 'Tax':
    case 'Total':
      return RowTypesIdentifier.FOOTER;
    default:
      return RowTypesIdentifier.BODY;
  }
}

export function toEngineRows(rows: TableRowType[], columns: TableColumnType[]): TableRowInput[] {
  const subtotalCol = columns.find((col) => col.columnType === ColumnTypeIdentifier.SUBTOTAL);
  const priceCol = columns.find((col) => col.columnType === ColumnTypeIdentifier.PRICE);

  return rows.map((row) => {
    const fieldValues: Record<string, string> = {};

    const rowIsGenerated = isGeneratedRow(row);

    columns.forEach((col) => {
      if (rowIsGenerated && (col.columnType === ColumnTypeIdentifier.PRICE || col.columnType === ColumnTypeIdentifier.MULTIPLIER)) return;

      const raw = row[col.field];
      if (raw === undefined || raw === null) return;
      const cleanValue = extractRawValueByColumnType(String(raw), col.columnType);
      if (cleanValue !== null && cleanValue !== '') {
        fieldValues[col.field] = cleanValue;
      }
    });

    // This row has no price of its own (no PRICE column, or the column exists but this
    // row's price cell is empty — and no manualSubtotalOverride): treat the subtotal
    // cell's existing number as the price.
    let manualSubtotalOverride = row.manualSubtotalOverride;
    const rowHasNoPriceValue = !priceCol || !row[priceCol.field];
    if (
      row.rowType === RowTypesIdentifier.BODY &&
      (manualSubtotalOverride === null || manualSubtotalOverride === undefined) &&
      rowHasNoPriceValue &&
      subtotalCol
    ) {
      const existingSubtotal = row[subtotalCol.field];
      const numericValue = existingSubtotal ? extractNumericValue(String(existingSubtotal)) : null;
      if (numericValue !== null) manualSubtotalOverride = String(numericValue);
    }

    return {
      id: row.id,
      rowType: toEngineRowType(row.rowType),
      billingFrequency: (() => {
        // Row-level is canonical (SP-3770 storage decision); the cell-level read
        // is a transition fallback for data written before the decision — remove
        // it at billingFrequency FF cleanup.
        const rowLevel = (row as EnhancedTableRowType).billingFrequency;
        const cell = subtotalCol ? row.cells?.[subtotalCol.field] : undefined;
        return (rowLevel ?? (cell && typeof cell === 'object' ? cell.billingFrequency : undefined) ?? 'one-time') as BillingFrequency;
      })(),
      discount: isEngineDiscount(row.discount) ? row.discount : null,
      isOptionalToggleAvailable: row.isOptionalToggleAvailable ?? false,
      isRowOptional: row.isRowOptional ?? false,
      manualSubtotalOverride: toEngineOverride(manualSubtotalOverride),
      fieldValues,
    };
  });
}

export function toEngineColumns(columns: TableColumnType[]): TableColumnInput[] {
  return columns.map((col) => ({
    field: col.field,
    columnType: col.columnType,
    width: col.width,
    editable: col.editable,
  }));
}

export function hasPricingInput(row: TableRowInput, columns: TableColumnInput[]): boolean {
  return row.manualSubtotalOverride != null || getPriceFromRow(row, columns) != null;
}
